/**
 * FAQ Schema JSON-LD Generator
 *
 * Generates valid Schema.org FAQPage JSON-LD markup for FAQ sections.
 * Follows Google's strict requirements for FAQPage structured data.
 *
 * Critical SEO Requirements:
 * - FAQPage schema enables rich snippets in Google search results
 * - Questions and answers must match visible content EXACTLY
 * - Answer text must be plain text (NO HTML tags)
 * - JSON must be strictly valid
 * - One FAQPage schema per page (multiple schemas = invalid)
 *
 * SEO Impact:
 * - Enables FAQ rich snippets in SERPs
 * - Shows Q&A directly in search results
 * - Significantly increases click-through rates
 * - Helps Google understand page intent
 */

export interface FAQItem {
  question: string;
  answer: string;
}

/**
 * Generate FAQPage Schema for a list of FAQ items
 *
 * @param faqItems - Array of FAQ question/answer pairs
 * @returns Schema.org FAQPage object
 *
 * @example
 * const faqItems = [
 *   { question: "What are AI watermarks?", answer: "AI watermarks are..." },
 *   { question: "How does it work?", answer: "Our tool scans..." }
 * ];
 * const schema = generateFAQSchema(faqItems);
 */
export function generateFAQSchema(faqItems: FAQItem[]): Record<string, any> {
  // Validate input
  if (!faqItems || faqItems.length === 0) {
    throw new Error('FAQ schema requires at least one FAQ item');
  }

  // Validate each FAQ item
  faqItems.forEach((item, index) => {
    if (!item.question || typeof item.question !== 'string') {
      throw new Error(`FAQ item ${index}: question is required and must be a string`);
    }
    if (!item.answer || typeof item.answer !== 'string') {
      throw new Error(`FAQ item ${index}: answer is required and must be a string`);
    }
  });

  // Ensure answers are plain text (strip any HTML if present)
  const sanitizedItems = faqItems.map((item) => ({
    question: stripHTML(item.question.trim()),
    answer: stripHTML(item.answer.trim()),
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: sanitizedItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

/**
 * Strip HTML tags from text to ensure plain text output
 * Google rejects FAQ schemas with HTML in answer text
 *
 * @param text - Text that may contain HTML
 * @returns Plain text without HTML tags
 */
function stripHTML(text: string): string {
  // Remove HTML tags
  let plainText = text.replace(/<[^>]*>/g, '');

  // Decode common HTML entities
  plainText = plainText
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'");

  // Remove extra whitespace
  plainText = plainText.replace(/\s+/g, ' ').trim();

  return plainText;
}

/**
 * Inject FAQ Schema JSON-LD into document head
 * Safe for both SSR and client-side rendering
 *
 * @param schema - FAQ schema object from generateFAQSchema()
 * @returns Cleanup function to remove the script element
 *
 * @example
 * useEffect(() => {
 *   const schema = generateFAQSchema(faqItems);
 *   const cleanup = injectFAQSchema(schema);
 *   return cleanup;
 * }, [faqItems]);
 */
export function injectFAQSchema(schema: Record<string, any>): () => void {
  // Guard against SSR
  if (typeof window === 'undefined') {
    return () => {}; // No-op cleanup function
  }

  // Remove any existing FAQ schema (SSR may have injected a generic one)
  // Page-specific FAQs should replace generic SSR FAQs
  const existingSchema = document.querySelector('script[type="application/ld+json"][data-schema-type="faq"]');
  if (existingSchema) {
    existingSchema.remove();
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-schema-type', 'faq'); // Mark as FAQ schema for duplicate detection

  try {
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
  } catch (error) {
    console.error('[FAQ Schema] Failed to inject FAQ schema:', error);
    return () => {}; // No-op cleanup
  }

  // Return cleanup function
  return () => {
    if (document.head.contains(script)) {
      document.head.removeChild(script);
    }
  };
}

/**
 * Validate FAQ schema structure
 * Useful for debugging and testing
 *
 * @param schema - Schema object to validate
 * @returns True if valid, false otherwise
 */
export function validateFAQSchema(schema: Record<string, any>): boolean {
  try {
    // Check required fields
    if (schema['@context'] !== 'https://schema.org') return false;
    if (schema['@type'] !== 'FAQPage') return false;
    if (!Array.isArray(schema.mainEntity)) return false;
    if (schema.mainEntity.length === 0) return false;

    // Validate each question
    for (const entity of schema.mainEntity) {
      if (entity['@type'] !== 'Question') return false;
      if (!entity.name || typeof entity.name !== 'string') return false;
      if (!entity.acceptedAnswer) return false;
      if (entity.acceptedAnswer['@type'] !== 'Answer') return false;
      if (!entity.acceptedAnswer.text || typeof entity.acceptedAnswer.text !== 'string') return false;

      // Check for HTML in answer (not allowed)
      if (entity.acceptedAnswer.text.includes('<') || entity.acceptedAnswer.text.includes('>')) {
        console.warn('[FAQ Schema] Answer contains HTML tags:', entity.acceptedAnswer.text.substring(0, 100));
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('[FAQ Schema] Validation error:', error);
    return false;
  }
}
